import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { integrationsApi, Integration } from '../api/integrations';
import { Link2, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { SiGithub, SiNotion, SiJira, SiAsana } from "react-icons/si";
import toast from 'react-hot-toast';

export interface IntegrationsPageProps {
  projectId?: string;
}

export const IntegrationsPage: React.FC<IntegrationsPageProps> = ({ projectId }) => {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [configuringProvider, setConfiguringProvider] = useState<string | null>(null);
  const [configValue, setConfigValue] = useState('');
  const [isSavingConfig, setIsSavingConfig] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  const fetchIntegrations = async () => {
    try {
      const data = await integrationsApi.getIntegrations();
      if (data.success) {
        setIntegrations(data.data);
      }
    } catch (error) {
      toast.error('Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
    
    // Check for OAuth callback in URL parameters
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    
    // Determine provider from previous click or state if we supported reliable state passing.
    // For simplicity, checking if we just returned from an auth flow:
    if (code) {
       // A proper implementation would decode the state to find the provider or use localStorage.
       const provider = localStorage.getItem('oauth_provider_intent') || 'github';
       handleOAuthCallback(provider, code);
    }
  }, [location]);

  const handleOAuthCallback = async (provider: string, code: string) => {
    try {
      toast.loading(`Connecting ${provider}...`, { id: 'oauth' });
      await integrationsApi.handleCallback(provider, code);
      toast.success(`${provider} connected successfully!`, { id: 'oauth' });
      localStorage.removeItem('oauth_provider_intent');
      // Clean URL
      window.history.replaceState({}, document.title, projectId ? `/projects/${projectId}?tab=integrations` : '/integrations');
      fetchIntegrations();
    } catch (error: any) {
      toast.error(`Failed to connect ${provider}`, { id: 'oauth' });
    }
  };

  const handleConnect = async (provider: 'github' | 'notion' | 'asana' | 'jira') => {
    try {
      localStorage.setItem('oauth_provider_intent', provider);
      const data = await integrationsApi.getConnectUrl(provider);
      if (data.success && data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      toast.error(`Failed to initiate connection for ${provider}`);
    }
  };

  const handleOpenConfig = async (provider: string) => {
    if (!projectId) return;
    try {
      setConfiguringProvider(provider);
      toast.loading('Fetching configuration...', { id: 'config' });
      const response = await integrationsApi.getProjectConfig(projectId, provider);
      if (response.success) {
        const config = response.config || {};
        if (provider === 'github') {
          setConfigValue(config.repo || '');
        } else if (provider === 'notion') {
          setConfigValue(config.database_id || '');
        } else if (provider === 'asana') {
          setConfigValue(config.project_id || '');
        } else if (provider === 'jira') {
          setConfigValue(config.project_key || '');
        }
        toast.dismiss('config');
      }
    } catch (error) {
      toast.error('Failed to load configuration', { id: 'config' });
    }
  };

  const handleSaveConfig = async () => {
    if (!projectId || !configuringProvider) return;
    try {
      setIsSavingConfig(true);
      const config: any = {};
      if (configuringProvider === 'github') {
        config.repo = configValue;
      } else if (configuringProvider === 'notion') {
        config.database_id = configValue;
      } else if (configuringProvider === 'asana') {
        config.project_id = configValue;
      } else if (configuringProvider === 'jira') {
        config.project_key = configValue;
      }
      
      const response = await integrationsApi.saveProjectConfig(projectId, configuringProvider, config);
      if (response.success) {
        toast.success('Configuration saved successfully');
        setConfiguringProvider(null);
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setIsSavingConfig(false);
    }
  };

  const handleDisconnect = async (provider: string) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider}? Your history will be preserved, but sync will be disabled.`)) {
      return;
    }

    try {
      toast.loading(`Disconnecting ${provider}...`, { id: 'disconnect' });
      const response = await integrationsApi.disconnect(provider);
      if (response.success) {
        toast.success(`${provider} disconnected successfully`, { id: 'disconnect' });
        fetchIntegrations();
      }
    } catch (error) {
      toast.error(`Failed to disconnect ${provider}`, { id: 'disconnect' });
    }
  };

  const providers = [
    { id: 'github', name: 'GitHub', icon: SiGithub, color: 'bg-gray-800', configLabel: 'Repository (owner/repo)', placeholder: 'facebook/react' },
    { id: 'notion', name: 'Notion', icon: SiNotion, color: 'bg-black', configLabel: 'Database ID', placeholder: 'e.g. 12345...' },
    // { id: 'asana', name: 'Asana', icon: SiAsana, color: 'bg-[#F06560]', configLabel: 'Project ID', placeholder: 'e.g. 98765...' },
    { id: 'jira', name: 'Jira Software', icon: SiJira, color: 'bg-[#0052CC]', configLabel: 'Project Key', placeholder: 'e.g. PROJ' }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <RefreshCw className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-10 animate-in fade-in duration-700">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-white/5">
        <div>
          <h1 className="text-3xl font-black text-white uppercase tracking-tight font-heading">Connectivity Matrix</h1>
          <p className="text-sm text-text-secondary font-medium mt-1">
            Bridge your strategic intelligence to external tools.
          </p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl border border-white/10 shadow-inner">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse shadow-[0_0_8px_rgba(255,107,0,0.5)]"></div>
          <span className="text-[10px] font-black uppercase tracking-widest text-text-secondary">Sync Status: Optimal</span>
        </div>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {providers.map((p, i) => {
          const isConnected = integrations.some((i) => i.provider === p.id)
          const Icon = p.icon

          return (
            <div 
              key={p.id} 
              className="bg-[#131B2C]/40 backdrop-blur-xl border border-white/10 p-8 rounded-3xl shadow-2xl flex flex-col justify-between group hover:border-primary/40 transition-all duration-500 hover:-translate-y-1 animate-in fade-in slide-in-from-bottom-4 duration-500"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center gap-5">
                  <div className={`p-4 rounded-2xl text-white ${p.color} shadow-lg relative overflow-hidden group-hover:scale-110 transition-transform duration-500`}>
                    <div className="absolute inset-0 bg-white/10 group-hover:bg-white/20 transition-colors"></div>
                    <Icon className="w-7 h-7 relative z-10" />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight font-heading">{p.name}</h3>
                    <div className="mt-1">
                      {isConnected ? (
                        <span className="text-[9px] font-black uppercase tracking-widest text-green-500 flex items-center gap-1.5">
                          <CheckCircle className="w-3.5 h-3.5" /> SECURE LINK established
                        </span>
                      ) : (
                        <span className="text-[9px] font-black uppercase tracking-widest text-text-secondary/40 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" /> LINK INACTIVE
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center justify-between pt-6 border-t border-white/5">
                <div className="flex gap-4">
                  {isConnected && (
                    <>
                      {projectId && (
                        <button 
                          onClick={() => handleOpenConfig(p.id)}
                          className="text-[10px] font-black text-primary hover:text-white transition-all uppercase tracking-widest flex items-center gap-2 group/btn"
                        >
                          <RefreshCw className="w-3.5 h-3.5 group-hover:rotate-180 transition-transform duration-500" />
                          Configure
                        </button>
                      )}
                      <button 
                        onClick={() => handleDisconnect(p.id)}
                        className="text-[10px] font-black text-text-secondary/40 hover:text-red-500 transition-all uppercase tracking-widest"
                      >
                        Disconnect
                      </button>
                    </>
                  )}
                </div>

                {isConnected ? (
                   <div className="p-2 bg-green-500/10 rounded-lg border border-green-500/20">
                     <CheckCircle className="w-4 h-4 text-green-500 shadow-[0_0_10px_rgba(34,197,94,0.3)]" />
                   </div>
                ) : (
                  <button
                    onClick={() => handleConnect(p.id as any)}
                    className="px-6 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-[10px] font-black uppercase tracking-widest text-white transition-all hover:border-primary/50 hover:shadow-[0_0_15px_rgba(255,107,0,0.2)]"
                  >
                    CONNECT INTEGRATION
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Configuration Modal */}
      {configuringProvider && (
        <div className="fixed inset-0 bg-[#0B0F19]/80 backdrop-blur-md flex items-center justify-center z-50 p-6">
          <div className="bg-[#131B2C] border border-white/10 rounded-3xl shadow-3xl max-w-md w-full p-8 animate-in zoom-in-95 duration-200 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-[100px] -z-10 transition-all group-hover:bg-primary/10"></div>
            
            <div className="flex justify-between items-center mb-8 pb-4 border-b border-white/5">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg border border-primary/20">
                  <RefreshCw className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-xl font-black text-white uppercase tracking-tight font-heading">
                   Integration Settings
                </h3>
              </div>
              <button 
                onClick={() => setConfiguringProvider(null)} 
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-text-secondary hover:text-white hover:bg-white/10 transition-all shadow-inner"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-8">
              <div className="group/field">
                <label className="block text-[10px] font-black text-white uppercase tracking-[0.2em] mb-3 px-1">
                  {providers.find((p) => p.id === configuringProvider)?.configLabel || 'Target Address'}
                </label>
                <div className="relative">
                  <input 
                    type="text" 
                    value={configValue}
                    onChange={(e) => setConfigValue(e.target.value)}
                    placeholder={providers.find((p) => p.id === configuringProvider)?.placeholder || 'Enter coordinates...'}
                    className="w-full bg-[#0B0F19]/60 border border-white/10 rounded-2xl px-5 py-4 text-sm font-medium text-white focus:outline-none focus:border-primary/50 focus:ring-4 focus:ring-primary/10 transition-all placeholder:text-text-secondary/20 shadow-inner"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary/20">
                    <Link2 className="w-4 h-4" />
                  </div>
                </div>
                {configuringProvider === 'github' && (
                  <p className="text-[10px] font-bold text-text-secondary/30 mt-3 flex items-center gap-1.5 uppercase tracking-wider">
                    <AlertCircle className="w-3 h-3 text-primary" />
                    Target format: <code>owner/repository</code>
                  </p>
                )}
                {configuringProvider === 'jira' && (
                  <p className="text-[10px] font-bold text-text-secondary/30 mt-3 flex items-center gap-1.5 uppercase tracking-wider">
                    <AlertCircle className="w-3 h-3 text-primary" />
                    Target format: <code>PROJECT_KEY</code>
                  </p>
                )}
              </div>
              
              <div className="flex flex-col gap-3 pt-4">
                <button 
                  onClick={handleSaveConfig}
                  disabled={isSavingConfig || !configValue}
                  className="w-full h-14 bg-gradient-to-r from-primary to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-2xl shadow-[0_0_20px_rgba(255,107,0,0.3)] font-black uppercase tracking-[0.3em] text-[10px] disabled:opacity-20 disabled:cursor-not-allowed flex items-center justify-center gap-3 transition-all active:scale-[0.98]"
                >
                  {isSavingConfig ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin text-white" />
                      SYNCHRONIZING...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      COMMIT CHANGES
                    </>
                  )}
                </button>
                <button 
                  onClick={() => setConfiguringProvider(null)}
                  className="w-full py-4 text-[10px] font-black text-text-secondary/40 hover:text-white transition-all uppercase tracking-widest"
                >
                  Cancel Operation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
